import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface BackgroundMedia {
  id: string;
  name: string;
  type: 'image' | 'video';
  url: string;
  opacity: number;
  is_active: boolean;
  display_order: number;
  duration_seconds: number;
}

interface BackgroundMediaProps {
  enableFadeAnimation?: boolean;
}

const BackgroundMedia = ({ enableFadeAnimation = false }: BackgroundMediaProps) => {
  const [videoOpacity, setVideoOpacity] = useState(1);

  // Fade animation effect
  useEffect(() => {
    if (enableFadeAnimation) {
      // Wait 2 seconds, then fade to 0.5 over 1 second
      const timer = setTimeout(() => {
        setVideoOpacity(0.5);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [enableFadeAnimation]);

  const { data: media = [], isLoading, error } = useQuery({
    queryKey: ['background-media'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('background_media')
        .select('*')
        .eq('is_active', true)
        .order('display_order');

      if (error) throw error;
      return data as BackgroundMedia[];
    },
    refetchInterval: 30000 // Refetch every 30 seconds to pick up changes
  });

  const activeMedia = media.filter(m => m.is_active);

  // Remove duplicates by ID to prevent React key warnings
  const uniqueMedia = activeMedia.reduce((acc: BackgroundMedia[], current) => {
    const exists = acc.find(item => item.id === current.id);
    if (!exists) {
      acc.push(current);
    }
    return acc;
  }, []);

  if (isLoading) return null;
  if (error) return null;
  if (uniqueMedia.length === 0) return null;

  return (
    <div className="fixed inset-0 z-[1] overflow-hidden pointer-events-none">
      {/* Render all active media items layered together */}
      {uniqueMedia.map((media) => (
        <div 
          key={media.id}
          className="absolute inset-0"
          style={{ opacity: media.opacity }}
        >
          {media.type === 'video' ? (
            <video
              className="w-full h-full object-cover"
              style={{
                opacity: enableFadeAnimation ? videoOpacity : media.opacity,
                transition: 'opacity 1s ease-in-out'
              }}
              src={media.url}
              autoPlay
              muted
              loop
              playsInline
            />
          ) : (
            <img
              className="w-full h-full object-cover"
              src={media.url}
              alt={media.name}
            />
          )}
        </div>
      ))}
    </div>
  );
};

export default BackgroundMedia;